import { has } from "lodash"

import {
  ISlugAndNumericIdentifierModel,
  ITransitionableModel,
  IMediaObject,
  IRI,
  CostSubCategory,
  INumericIdentifierModel,
  IProject,
  SelfAssessment,
  ObligationState,
  IPartialProjectData,
  IBankDetails,
  ITenant,
  IUser,
} from "@api/schema"
import { DocumentCategory, DocumentTemplateCategoryName, IDocument } from "@modules/documents/src"
import { ILockableModel } from "@redux/usecases/lock/schema-definitions"

import { AvailableContentItems } from "./content-items"

// #region challenge enums
/**
 * This enum defines available Types of a Challenge.
 *
 * Notation is specified by the API.
 */
export enum ChallengeType {
  BasicChallenge = "BasicChallenge",
  Fund = "Fund"
}

/**
 * available selection process options to select proposals for granting
 *
 * may be extended by Jury, Voting etc.
 */
export enum ChallengeSelectionProcess {
  /** basic means: selection by manager */
  Basic = "basic"
}

/**
 * ChallengeState frames the possible states of a challenge.
 *
 * Notation is specified by the API.
 */
export enum ChallengeState {
  Created = "created",
  Published = "published",
  Open = "open",
  Paused = "paused",
  Deciding = "deciding",
  FulFilling = "fulfilling",
  Clearing = "clearing",
  Finished = "finished",
}

/**
 * ChallengeTransitionState frames the possible transitions of a challenge.
 *
 * Notation is specified by the API.
 */
export enum ChallengeTransitionState {
  Publish = "publish",
  Open = "open",
  Pause = "pause",
  Close = "close",
  Grant = "grant",
  Conclude = "conclude",
  Finish = "finish",
}
// #endregion

// #region challenge types

/**
 * An IAbstractChallenge contains the basic data for each concrete challenge.
 */
interface IAbstractChallenge<Type extends ChallengeType> extends ISlugAndNumericIdentifierModel,
  ITransitionableModel<ChallengeTransitionState>,
  ILockableModel {
  "@type"?: Type
  /**
   * List of definitions of attachments a proposal has to provide to be submitted/approvable.
   * @deprecated fetch via collection call
   */
  attachmentDefinitions?: IAttachmentDefinition<AttachmentDefinitionType>[] // list of definitions of attachments, that must or should be provided to the proposal
  /**
   * Date-time, when the Challenge has been created.
   * */
  createdAt?: Date | string
  /**
   * User who created the challenge.
   */
  createdBy?: IUser
  /**
   * Description of criterias, which a proposal has to match to be approvable for the challenge.
   */
  criteria?: string[]
  /**
   * Description of the challenge.
   */
  description?: string
  /**
   * A challengeManager can decide that a challenge
   * may accept proposals to be submitted via the Projektfabrik platform (direkt submission)
   * or via other channels (e.g. on paper),
   * but those proposals are also managed by the Projektfabrik challenge system.
   * directSubmission === true means, proposals are accepted via Projektfabrik submission only.
   */
  directSubmission?: boolean
  /**
   * Date when the implementation process for projects starts.
   */
  implementationBegin?: Date | string
  /**
   * Logo of the challenge.
   */
  logo?: IMediaObject
  /**
   * Name of the challenge
   */
  name?: string
  /**
   * Tenant to which a challenge belongs.
   */
  provider?: ITenant | IRI
  /**
   * Geographical region where the challenge is valid.
   */
  region?: string
  /**
   * Type of process that is used for selecting proposals to be approved.
   */
  selectionProcess?: ChallengeSelectionProcess
  /**
   * List of the sponsors who provided the challenge (or its money).
   */
  sponsors?: ISponsorDTO[]
  /**
   * Current state of the challenge.
   * read-only. Is changed by the transition of a challenge.
   */
  state?: ChallengeState
  /**
   * Date after which submitting proposals is allowed: start of the submission period.
   */
  submissionBegin?: Date | string
  /**
   * Date when the submission period ends.
   */
  submissionEnd?: Date | string

  // TODO challenge: neue Properties, die es zu dokumentieren und einzuarbeiten gilt
  bankDetails?: IBankDetails
  /**
   * Geographical data to visualize the region of the challenge.
   */
  geoJson?: GeoJSON.Feature | GeoJSON.FeatureCollection
  // TODO challenge: docu
  openForAll?: boolean
  /**
   * Is this challenge interested in new sponsors?
   */
  openForSponsors?: boolean
  /**
   * Number of proposals, even unsubmitted proposals from locked|inactive|deactivated projects.
   * // TODO challenge: siehe Entity/Challenge/AbstractChallenge im Backend
   */
  proposalCount?: number
  /**
   * Date when the challenge was updated lately.
   */
  updatedAt?: Date | string
  /**
   * User who lately updated the challenge.
   */
  updatedBy?: Date | string
  /** ID of the entity at the external provider (if it comes from an external provider, e.g. SINN Sachsen); probably a number. */
  externalIdentifier?: string
  /** Short name of the external provider (if the entity comes from an external provider, e.g. SINN Sachsen). */
  externalProvider?: string
}

/**
 * An IChallenge represents a challenge, consisting of its name, description, timeline concretization-questions, needed
 * attachments for proposals and a list of received proposal.
 */
export type IBasicChallenge = IAbstractChallenge<ChallengeType.BasicChallenge>

export const CURRENT_SPONSOR_DTO_TYPE = "SponsorDto_v1"

export interface ISponsorDTO {
  "@type": typeof CURRENT_SPONSOR_DTO_TYPE
  name?: string
}

/**
 * A IProposalContent represents a single key of content (contentId), that is or is not provided to a proposal.
 * The inner ObligationState defines, if this content must or could be included or if it is irrelevant.
 * @deprecated is not used anymore => IProjectPlanMetadataDtoV1 uses directly AvailableContentItems,
 */
export interface IProposalContent {
  contentId: AvailableContentItems
  requirement: ObligationState
  "@type": string
}

/**
 * An IFund is special challenge, which adds the option to support a project with money.
 * It only accepts fund applications as a proposal.
 */
export interface IFund extends IAbstractChallenge<ChallengeType.Fund> {
  /**
   * budget of the fund
   */
  budget?: number
  /**
   * is it required to attach costTypes to ResourceRequirements by applicants?
   */
  costTypesRequired?: boolean
  /**
   * which custom cost types for materialcosts are required by the fund
   */
  customMaterialCostTypes?: string[]
  fundingDuration?: number
  fundingRate?: number
  /**
   * what is the maximum rate per hour, a fund accepts for ownResources
   * (kalkulatorischer Stundensatz für erbrachte Eigenleistungen)
   */
  maxFundedHourlyRate?: number
  maximumGrant?: number
  minimumGrant?: number
  /**
   * is it possible to use ownResources (Eigenleistungen) in an application, if the fund-quota is less than 100%?
   */
  ownContributionsAccepted?: boolean
  /**
   * which CostSubCategory have to be assigned to ResourceRequirements of Type "material costs"
   */
  supportedMaterialCostTypes?: CostSubCategory[]
}

/** UnionType of all types of challenges. */
export type IChallenge = IBasicChallenge | IFund
// #endregion

// #region proposal enums
/**
 * This enum defines available states for the recommendation of a proposal.
 *
 * Notation is specified by the API.
 */
export enum ApprovalRecommendation {
  Open = "open",
  Recommended = "recommended",
  Alternate = "alternate",
  Reject = "reject"
}

/**
 * A ProposalState frames the possible states of an proposal for a challenge.
 *
 * Notation is specified by the API.
 */
export enum ProposalState {
  Preparing = "preparing",
  /** a proposal that is finished was submitted before (and may have had other states "after" submitted before being finished)  */
  Finished = "finished", // @todo eventuell State umbenennen in proposal.submissionstate
  Submitted = "submitted",
}

export const isProposalState = (state: string): state is ProposalState =>
  Object.values(ProposalState).includes(state as ProposalState)

/**
 * States of a proposal transition.
 *
 * Notation is specified by the API.
 */
export enum ProposalTransitionState {
  Submit = "submit",
  Finish = "finish"
}

/**
 * Types of a proposal, based on the Challenge/Fund it is connected to.
 *
 * Notation is specified by the API.
 */
export enum ProposalType {
  BasicProposal = "BasicProposal",
  FundApplication = "FundApplication"
}

/**
 * ApplicationState describes the additional states of a FundApplication, additional to ProposalState
 * coming after ProposalState.Submitting
 *
 * Notation is specified by the API.
 */
export enum ApplicationState {
  AwaitingAcception = 'awaiting_acceptation',
  Fulfilling = 'fulfilling',
  Postprocessing = 'postprocessing'
}

export const isApplicationState = (state: string): state is ApplicationState =>
  Object.values(ApplicationState).includes(state as ApplicationState)

/**
 * A FundApplication has the same states like a proposal regarding its progress to a challenge,
 * extended by additional states of a FundApplication.
 *
 * Because it is not possible in typescript to extend an enum the Uniontype is used as a workaround.
 * This solution mirrors the relationship of Proposal and FundApplication in the Backend.
 */
export type FundApplicationState = ProposalState | ApplicationState
// #endregion

// #region proposal types
type IAbstractProposal<
  Type extends ProposalType,
  State extends ProposalState | FundApplicationState,
  SubmissionData extends IProposalSubmissionData | IApplicationSubmissionData
> =
  // define the properties of the type
  {
    "@type"?: Type
    /**
     * is this proposal activated for the current project?
     * @deprecated
     */
    active?: boolean
    /** is the proposal recommended for approvement? */
    approvalRecommendation?: ApprovalRecommendation
    /** is the proposal approved? ("bewilligt") */
    approved?: boolean
    /**
     * list of attachments belonging to this proposal
     * @deprecated  fetched via SubResource-Endpoint
     */
    attachments?: IProposalAttachment[]
    /** challenge to which the proposal belongs: Entity or IRI */
    challenge?: IChallenge | IRI
    /**
     * Date-time, when the Proposal has been created.
     * */
    createdAt?: Date | string
    /**
     * User who created the proposal.
     */
    createdBy?: IUser
    /**
     * if the challenge has a voting: is this proposal licensed to take part in the voting?
     * @deprecated
     */
    eligibleForVoting?: boolean
    /** rank of the proposal within its recommondation */
    preselectionOrder?: number
    /**
     * project the proposal belongs to: Entity or IRI
     *
     * NOTE: if the proposal fetched by the manager by his proposal endpoint
     * this property is set with project entity stub data: @id, followerCount, memberCount, name, id, usedMemberRole.
     * For team members this property is undefined, b/c it is assumed that the user fetched the project prior to the proposal
     * or the proposal is delivered by the project.
     *
     * @todo: clarify: can this propery ever have an IRI?
     */
    project?: IProject | IRI
    /**
     * Proposal-PDF, adopted to the chosen ContentManagement of a given challenge
     * @deprecated
     */
    proposalPdfFile?: IMediaObject
    /** self assessment about the progress of the proposal */
    proposalSelfAssessment?: SelfAssessment
    /**
     * public readable submitted data
     * @deprecated
     */
    publicSubmissionData?: SubmissionData
    /**
     * rank of the proposal in an (optional) voting of the challenge
     * @deprecated
     */
    publicVotingResult?: number
    /**
     * state of the proposal
     * is redefined for IFundApplication
     *
     * @todo: maybe rename entity attribute to submissionState to be more precise -> to be done in backend first: FCP-1188
     */
    state?: State
    /** all (even non-public) submitted data */
    submissionData?: SubmissionData
    /**
     * all-inclusive PDF-file with all project-data as snapshot from the moment of the proposal-generation
     * @deprecated
     */
    teamPdfFile?: IMediaObject
    /**
     * additional application text for taking part of a voting
     * @deprecated
     */
    votingApplication?: string
  } & INumericIdentifierModel extends infer AbstractProposal
  ? (
    // define which generic type parameter combinations are allowed
    [
      Type extends ProposalType.BasicProposal ? "basicProposal" : false,
      State extends ProposalState ? "basicProposalState" : false,
      SubmissionData extends IProposalSubmissionData ? "basicProposalSubmissionData" : false
    ] extends ["basicProposal", "basicProposalState", "basicProposalSubmissionData"]
    ? AbstractProposal
    : [
      Type extends ProposalType.FundApplication ? "fundApplication" : false,
      State extends FundApplicationState ? "fundApplicationState" : false,
      SubmissionData extends IApplicationSubmissionData ? "fundApplicationSubmissionData" : false
    ] extends ["fundApplication", "fundApplicationState", "fundApplicationSubmissionData"]
    ? AbstractProposal
    : never
  )
  : never
/**
 * An IBasicProposal represents an proposal for a challenge.
 */
export type IBasicProposal = IAbstractProposal<ProposalType.BasicProposal, ProposalState, IProposalSubmissionData>

/**
 * An IFundApplication represents an application for a funding of a fund.
 */
export interface IFundApplication extends IAbstractProposal<
  ProposalType.FundApplication,
  ProposalState | ApplicationState,
  IApplicationSubmissionData> {
  requestedFunding?: number
}

/**
 * UnionType of all types of proposals
 *
 * // TODO challenge check where this union type should be used and where not
 */
export type IProposal = IBasicProposal | IFundApplication

/**
 * An IProposalSubmissionData represents an proposal to a challenge.
 * It collects the data stored with each submitted proposal, an immutable copy of most project properties.
 *
 * Does explicitely not extends IProject and IProposal, b/c over 20 properties from IProject and IProposal
 * are not part of IProposalSubmissionData.
 * @todo multi maybe change interface hierarchy? IProject should extends an IProjectSubmissionData?
 */
export interface IProposalSubmissionData extends Required<IPartialProjectData<string[], string>> {
  /**
   * When submitted, categories are injected as category names, not IRIs,
   * to make sure, they are stable even when program categories change.
   */
  categories: string[]
  challengeName: string
  challengeSlug: string
  implementationBegin: string
  programName: string
  projectId?: number
  slug: string
  submissionDate?: Date | string
  submittedBy?: {
    userId: number
    username: string
    firstName: string
    lastName: string
  }
  teamContactName: string
  teamContactEmail: string
}

/**
 * An IApplicationSubmissionData represents an application to a fund.
 */
export interface IApplicationSubmissionData extends IProposalSubmissionData {
  requestedFunding: number
}
// #endregion

// #region AttachmentDefinition

/**
 * Types of all available AttachmentDefinitions.
 */
export enum AttachmentDefinitionType {
  Document = "document",
  File = "file",
  ProjectPlan = "projectPlan"
}

/**
 * Dto used by some AttachmentDefinitionMetadataDtos.
 */
interface IMaxSizeAndMimeTypesDto {
  maxSize?: number
  mimeTypes?: string[]
}

/**
 * Dto used by some AttachmentDefinitionMetadataDtos.
 */
interface IAcceptsUploadedFilesDto {
  /**
   * Flag for the challengeManagers to determine, if instead only allowing
   * autogenerated PDFs as attachment, the users may also upload their own
   * files (or select an existing TeamUpload).
   */
  acceptsUploadedFiles?: boolean
}

/** Mapping AttachmentDefinitionType to Metadata Type */
type MetadataTypeMap = {
  [AttachmentDefinitionType.Document]: "DocumentMetadataDto_v1"
  [AttachmentDefinitionType.File]: "FileMetadataDto_v1"
  [AttachmentDefinitionType.ProjectPlan]: "ProjectPlanMetadataDto_v1"
}

/** Currently used Metadata-DTO types for AttachmentDefinitions */
type AttachmentDefinitionMetadataDtoType<T extends AttachmentDefinitionType> = MetadataTypeMap[T]

export interface IAbstractAttachmentDefinitionMetadataDto<GenericAttachmentDefinitionType extends AttachmentDefinitionType> extends IMaxSizeAndMimeTypesDto {
  "@type": AttachmentDefinitionMetadataDtoType<GenericAttachmentDefinitionType>
}

/** Those data is needed to define a Document based AttachmentDefinition */
export interface IDocumentMetadataDtoV1 extends
  IAbstractAttachmentDefinitionMetadataDto<AttachmentDefinitionType.Document>,
  IAcceptsUploadedFilesDto {
  category?: DocumentCategory
  template?: DocumentTemplateCategoryName
}

export const isIDocumentMetadataDtoV1 = (metadataDto: unknown): metadataDto is IDocumentMetadataDtoV1 =>
  typeof metadataDto === "object" && metadataDto !== null && (metadataDto as IDocumentMetadataDtoV1)["@type"] === "DocumentMetadataDto_v1"

/** Those data is needed to define a File based AttachmentDefinition */
export interface IFileMetadataDtoV1 extends
  IAbstractAttachmentDefinitionMetadataDto<AttachmentDefinitionType.File> {
  maxCount?: number
}

export const isIFileMetadataDtoV1 = (metadataDto: unknown): metadataDto is IFileMetadataDtoV1 =>
  typeof metadataDto === "object" && metadataDto !== null && (metadataDto as IFileMetadataDtoV1)["@type"] === "FileMetadataDto_v1"

/** Those data is needed to define a ProjectPlan based AttachmentDefinition. */
export interface IProjectPlanMetadataDtoV1 extends
  IAbstractAttachmentDefinitionMetadataDto<AttachmentDefinitionType.ProjectPlan>,
  IAcceptsUploadedFilesDto {
  /** Required or wanted contents for a proposal for that specific challenge. */
  contentIds?: AvailableContentItems[]
}

/**
 * Checks if the "@type" property of a given metaDTO has the current project plan metadata Dto type.
 *
 * @param metadataDto
 * @returns true, the given metaDTO is an IProjectPlanMetadataDtoV1
 */
export const isIProjectPlanMetadataDtoV1 = (metadataDto: unknown): metadataDto is IProjectPlanMetadataDtoV1 =>
  typeof metadataDto === "object" && metadataDto !== null && (metadataDto as IProjectPlanMetadataDtoV1)["@type"] === "ProjectPlanMetadataDto_v1"

type AttachmentMetadataMap = {
  [AttachmentDefinitionType.Document]: IDocumentMetadataDtoV1
  [AttachmentDefinitionType.File]: IFileMetadataDtoV1
  [AttachmentDefinitionType.ProjectPlan]: IProjectPlanMetadataDtoV1
}

// Erzwinge exakte Übereinstimmung — liefert einen Typfehler, wenn Keys fehlen/dazukommen.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
// type _EnsureMapExactMatch = [Exclude<AttachmentDefinitionType, keyof AttachmentMetadataMap>] extends [never]
//   ? [Exclude<keyof AttachmentMetadataMap, AttachmentDefinitionType>] extends [never]
//   ? true
//   : ["Extra keys", Exclude<keyof AttachmentMetadataMap, AttachmentDefinitionType>]
//   : ["Missing keys", Exclude<AttachmentDefinitionType, keyof AttachmentMetadataMap>]

export type IAttachmentDefinitionMetadataDto<T extends AttachmentDefinitionType> =
  T extends keyof AttachmentMetadataMap ? AttachmentMetadataMap[T] : IAbstractAttachmentDefinitionMetadataDto<T>

/**
 * An IAttachmentDefinition represents a definition of an attachment, that must/should/could attached
 * to a proposal for a challenge.
 */
export interface IAttachmentDefinition<GenericAttachmentDefinitionType extends AttachmentDefinitionType = AttachmentDefinitionType> extends INumericIdentifierModel {
  "@type"?: "AttachmentDefinition"
  challenge?: IChallenge | IRI
  content?: string
  description?: string
  /** @deprecated saved in metadata when metadata is type of IFileMetadataDtoV1  */
  maxSize?: number
  metadata?: IAttachmentDefinitionMetadataDto<GenericAttachmentDefinitionType>
  /** @deprecated saved in metadata when metadata is type of IFileMetadataDtoV1 */
  mimeTypes?: string[]
  outdated?: boolean
  required?: boolean
  type?: GenericAttachmentDefinitionType
}

export const isAttachmentDefinition = (attachmentDefinition: object): attachmentDefinition is IAttachmentDefinition<AttachmentDefinitionType> =>
  has(attachmentDefinition, "@type") && attachmentDefinition["@type"] === "AttachmentDefinition"

export type IFileAttachmentDefinition = IAttachmentDefinition<AttachmentDefinitionType.File>

export const isFileAttachmentDefinition = (attachmentDefinition: object): attachmentDefinition is IFileAttachmentDefinition =>
  isAttachmentDefinition(attachmentDefinition) && attachmentDefinition.type === AttachmentDefinitionType.File

export type IProjectPlanAttachmentDefinition = IAttachmentDefinition<AttachmentDefinitionType.ProjectPlan>

export const isProjectPlanAttachmentDefinition = (attachmentDefinition: object): attachmentDefinition is IProjectPlanAttachmentDefinition =>
  isAttachmentDefinition(attachmentDefinition) && attachmentDefinition.type === AttachmentDefinitionType.ProjectPlan

export type IDocumentAttachmentDefinition = IAttachmentDefinition<AttachmentDefinitionType.Document>

export const isDocumentAttachmentDefinition = (attachmentDefinition: object): attachmentDefinition is IDocumentAttachmentDefinition =>
  isAttachmentDefinition(attachmentDefinition) && attachmentDefinition.type === AttachmentDefinitionType.Document
// #endregion

// #region ProposalAttachment
/** Currently related ProposalAttachmentType for AttachmentDefinitions */
export type ProposalAttachmentType<GenericAttachmentDefinitionType extends AttachmentDefinitionType = AttachmentDefinitionType> = [
  GenericAttachmentDefinitionType extends AttachmentDefinitionType.Document ? "document" : false,
  GenericAttachmentDefinitionType extends AttachmentDefinitionType.File ? "file" : false,
  GenericAttachmentDefinitionType extends AttachmentDefinitionType.ProjectPlan ? "projectplan" : false,
] extends infer ChooseMetaDataDtoType ?
  (
    ChooseMetaDataDtoType extends ["document", false, false]
    ? "DocumentProposalAttachment"
    : ChooseMetaDataDtoType extends [false, "file", false]
    ? "FileProposalAttachment"
    : ChooseMetaDataDtoType extends [false, false, "projectplan"]
    ? "ProjectPlanProposalAttachment"
    : "DocumentProposalAttachment" | "FileProposalAttachment" | "ProjectPlanProposalAttachment"
  ) : never


interface IAbstractProposalAttachment<
  GenericAttachmentDefinitionType extends AttachmentDefinitionType
> extends INumericIdentifierModel {
  "@type"?: ProposalAttachmentType<GenericAttachmentDefinitionType>
  definition?: IAttachmentDefinition<GenericAttachmentDefinitionType>
  file?: File | IMediaObject
  /**
   * Can contain additional data, like the rendered contentIds for
   * ProjectPlan attachments, set by the backend, not writeable by the client.
   */
  metadata?: GenericAttachmentDefinitionType extends AttachmentDefinitionType.ProjectPlan
  ? Omit<IProjectPlanMetadataDtoV1, "@type"> | []
  : never
  proposal?: IProposal | IFundApplication | IRI
  readOnly?: boolean
}

export interface IDocumentProposalAttachment extends IAbstractProposalAttachment<
  AttachmentDefinitionType.Document
> {
  document: IDocument
}

/**
 * Checks if the "@type" property of a given proposalAttachment is a `ProposalAttachmentType.DocumentProposalAttachment`
 *
 * @param proposalAttachment
 * @returns true, the given proposalAttachment is an IDocumentProposalAttachment
 */
export const isDocumentProposalAttachment = (proposalAttachment: IProposalAttachment): proposalAttachment is IDocumentProposalAttachment =>
  proposalAttachment["@type"] === "DocumentProposalAttachment"

export type IFileProposalAttachment = IAbstractProposalAttachment<
  AttachmentDefinitionType.File
>

/**
 * Checks if the "@type" property of a given proposalAttachment is a `ProposalAttachmentType.FileProposalAttachment`
 *
 * @param proposalAttachment
 * @returns true, the given proposalAttachment is an IFileProposalAttachment
 */
export const isFileProposalAttachment = (proposalAttachment: IProposalAttachment): proposalAttachment is IFileProposalAttachment =>
  proposalAttachment["@type"] === "FileProposalAttachment"

export type IProjectPlanProposalAttachment = IAbstractProposalAttachment<
  AttachmentDefinitionType.ProjectPlan>

/**
 * Checks if the "@type" property of a given proposalAttachment is a `ProposalAttachmentType.ProjectPlanProposalAttachment`
 *
 * @param proposalAttachment
 * @returns true, the given proposalAttachment is an IProjectPlanProposalAttachment
 */
export const isProjectPlanProposalAttachment = (proposalAttachment: IProposalAttachment): proposalAttachment is IProjectPlanProposalAttachment =>
  proposalAttachment["@type"] === "ProjectPlanProposalAttachment"

/**
 * An IProposalAttachment represents an attachment to an proposal to a challenge. It is possibly connected to
 * an AttachmentDefinition of the challenge, where the proposal is created for.
 */
export type IProposalAttachment = IAbstractProposalAttachment<AttachmentDefinitionType>

// #endregion ProposalAttachment